import React, { useState } from 'react';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import GuestPickerModal, { ReservationUserOption } from '../GuestPickerModal/GuestPickerModal';
import '../Reservations/Reservations.css';

interface Props {
  hotelId: number;
  roomId: number;
  roomNumber: string;
  checkInDate: string;
  checkOutDate: string;
  rateOptions: Array<{
    roomRateId: number;
    ratePlanId: number;
    ratePlanName: string;
    pricePerNight: number;
    totalPrice: number;
    nightCount: number;
    isRefundable: boolean;
  }>;
  onSaved: () => void;
  onClose: () => void;
}

const CreateReservationModal = ({ hotelId, roomId, roomNumber, checkInDate, checkOutDate, rateOptions, onSaved, onClose }: Props) => {
  const { i18n } = useTranslation();
  const [selectedProfile, setSelectedProfile] = useState<ReservationUserOption | null>(null);
  const [showProfilePicker, setShowProfilePicker] = useState(false);
  const [selectedRatePlanId, setSelectedRatePlanId] = useState(rateOptions[0]?.ratePlanId ? String(rateOptions[0].ratePlanId) : '');

  const selectedRateOption = rateOptions.find((option) => String(option.ratePlanId) === selectedRatePlanId) ?? rateOptions[0] ?? null;

  const createReservation = async () => {
    if (!selectedProfile?.id || !checkOutDate || !selectedRateOption) {
      toast.warning(i18n.t('reservationDesk.fillAllFields'));
      return;
    }

    try {
      await axiosInstance.post(ApiConstants.reservation.create, {
        hotelId,
        roomId: roomId,
        ratePlanId: selectedRateOption?.ratePlanId ?? 0,
        checkInDate: checkInDate,
        checkOutDate: checkOutDate,
        guests: [
          {
            userId: selectedProfile.userId ?? Number(selectedProfile.id),
            isPrimary: true,
            guestType: 1,
          },
        ],
      });

      toast.success(i18n.t('reservationDesk.reservationCreated'));
      onSaved();
      onClose();
    } catch {
      toast.error(i18n.t('reservationDesk.failedToCreateReservation'));
    }
  };

  return (
    <div className="reservation-modal">
      <div className="reservation-modal-content reservation-create-modal">
        <div className="reservation-modal-top">
          <span className="reservation-kicker">{i18n.t('reservationDesk.newBooking')}</span>
        <h3>{i18n.t('reservationDesk.createReservation')}</h3>
          <p>{i18n.t('reservationDesk.createReservationSubtitle')}</p>
        </div>

        <div className="reservation-summary">
          <div className="reservation-summary-item">
            <span>{i18n.t('reservationDesk.room')}</span>
            <strong>{roomNumber}</strong>
          </div>

          <div className="reservation-summary-item">
            <span>{i18n.t('reservationDesk.checkIn')}</span>
            <strong>{checkInDate}</strong>
          </div>

          <div className="reservation-summary-item">
            <span>{i18n.t('reservationDesk.checkOut')}</span>
            <strong>{checkOutDate}</strong>
          </div>
        </div>

        <div className="modal-field">
          <label>{i18n.t('reservationDesk.ratePlan')}</label>
          <select value={selectedRatePlanId} onChange={(e) => setSelectedRatePlanId(e.target.value)} disabled={rateOptions.length === 0}>
            {rateOptions.length === 0 && <option value="">{i18n.t('reservationDesk.autoRatePlan')}</option>}
            {rateOptions.map((option) => (
              <option key={option.ratePlanId} value={option.ratePlanId}>
                {option.ratePlanName} - {option.totalPrice.toFixed(2)}
              </option>
            ))}
          </select>
        </div>

        {selectedRateOption && (
          <div className="reservation-summary">
            <div className="reservation-summary-item">
              <span>{i18n.t('reservationDesk.pricePerNight')}</span>
              <strong>{selectedRateOption.pricePerNight.toFixed(2)}</strong>
            </div>
            <div className="reservation-summary-item">
              <span>{i18n.t('reservationDesk.nights')}</span>
              <strong>{selectedRateOption.nightCount}</strong>
            </div>
            <div className="reservation-summary-item">
              <span>{i18n.t('generic.total')}</span>
              <strong>{selectedRateOption.totalPrice.toFixed(2)}</strong>
            </div>
          </div>
        )}

        <div className="modal-field">
          <label>Reservation user</label>
          <button type="button" className="guest-select-trigger" onClick={() => setShowProfilePicker(true)}>
            {selectedProfile
              ? `${selectedProfile.firstName ?? ''} ${selectedProfile.lastName ?? ''}`.trim() || `#${selectedProfile.id}`
              : 'Select profile'}
          </button>
        </div>

        {selectedProfile && (
          <div className="guest-selected-card">
            <strong>{`${selectedProfile.firstName ?? ''} ${selectedProfile.lastName ?? ''}`.trim() || `#${selectedProfile.id}`}</strong>
            <span>{selectedProfile.email || selectedProfile.phone || 'No contact details'}</span>
            <span>{selectedProfile.userId ? `Linked user #${selectedProfile.userId}` : `Profile user #${selectedProfile.id}`}</span>
          </div>
        )}

        <div className="modal-actions">
          <button onClick={createReservation}>{i18n.t('generic.save')}</button>

          <button onClick={onClose}>{i18n.t('generic.cancel')}</button>
        </div>
      </div>

      {showProfilePicker && (
        <GuestPickerModal
          onClose={() => setShowProfilePicker(false)}
          onSelect={(profile) => {
            setSelectedProfile(profile);
            setShowProfilePicker(false);
          }}
        />
      )}
    </div>
  );
};

export default CreateReservationModal;
